import React from 'react';
import {fetchSourceFile} from './sourcesApi';
import {
    extractDataItemProcessingFields,
    isFileActivelyProcessing,
    resolveFileProcessingStatus,
} from './sourcesFileMetadata';

const POLL_INTERVAL_MS = 3000;

/**
 * Polls data-item detail while a file is processing and merges meta.processing into local state.
 */
export default function useSourceItemProcessingPoll({
    fileId,
    userId,
    fileDetails,
    enabled,
    forcePoll = false,
    onProcessingComplete,
}) {
    const [polledFields, setPolledFields] = React.useState(null);

    React.useEffect(() => {
        setPolledFields(null);
    }, [fileId]);

    const mergedFile = React.useMemo(() => ({
        ...(fileDetails || {}),
        ...(polledFields || {}),
        status: polledFields?.status ?? fileDetails?.status ?? null,
        meta: polledFields?.meta ?? fileDetails?.meta ?? null,
        processingStage: polledFields?.processingStage ?? fileDetails?.processingStage ?? null,
        processingError: polledFields?.processingError ?? fileDetails?.processingError ?? null,
    }), [fileDetails, polledFields]);

    const processingStatus = React.useMemo(
        () => resolveFileProcessingStatus(mergedFile),
        [mergedFile],
    );

    // Once the pipeline has settled (item processed or failed) we never poll —
    // not even when forcePoll is set for a transient PM-not-ready fetch — so
    // reopening a finished item doesn't churn the poll loop.
    const pipelineSettled = processingStatus?.kind === 'ready' || processingStatus?.kind === 'failed';
    const shouldPoll = Boolean(enabled && fileId && userId && !pipelineSettled && (
        forcePoll || isFileActivelyProcessing(mergedFile) || processingStatus?.kind === 'processing'
    ));

    React.useEffect(() => {
        if (!shouldPoll) {
            return undefined;
        }

        let cancelled = false;

        const poll = async () => {
            try {
                const payload = await fetchSourceFile({fileId, userId});
                if (cancelled) {
                    return;
                }
                const fields = extractDataItemProcessingFields(payload);
                setPolledFields(fields);

                const nextStatus = resolveFileProcessingStatus({
                    ...(fileDetails || {}),
                    ...fields,
                });
                if (nextStatus?.kind === 'ready') {
                    onProcessingComplete?.();
                }
                if (nextStatus?.kind === 'failed') {
                    onProcessingComplete?.({failed: true, errorMessage: nextStatus.errorMessage});
                }
            } catch (error) {
                if (!cancelled) {
                    console.warn('[useSourceItemProcessingPoll] Poll failed:', error?.message || error);
                }
            }
        };

        poll();
        const intervalId = window.setInterval(poll, POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [shouldPoll, fileId, userId, onProcessingComplete]);

    return {
        mergedFile,
        processingStatus,
        isPolling: shouldPoll,
    };
}
